import { ref, watch, type Ref } from 'vue';
import type {
  ConsultaDisponivel,
  ContextoAssistente,
  FerramentaExecutada,
  FerramentaSolicitada,
  IniciarResponse,
  MensagemChat,
  PerguntaApiRequest,
  RespostaIA
} from '@/types/api';
import {
  carregarConsultasDisponiveis,
  carregarControllersPermitidos,
  executarFerramenta
} from './useDadosVidas';

// Persistencia em localStorage — sobrevive a navegacao no MaisVidas legado
// (que e' MVC server-rendered, cada troca de modulo recarrega a pagina e
// destroi o estado em memoria do Vue). Chave isolada por usuarioID pra
// nao misturar conversas em maquinas compartilhadas.
const STORAGE_PREFIX = 'maisvidas.assistenteia';
const STORAGE_TTL_DIAS = 7;

interface EstadoPersistido {
  sessaoID: string | null;
  mensagens: MensagemChat[];
  ultimaAtualizacao: number;
}

function chaveStorage(usuarioID: number): string {
  return `${STORAGE_PREFIX}.u${usuarioID}.state`;
}

function carregarDoStorage(usuarioID: number): EstadoPersistido | null {
  try {
    const raw = localStorage.getItem(chaveStorage(usuarioID));
    if (!raw) return null;
    const state = JSON.parse(raw) as EstadoPersistido;
    // TTL — descarta state antigo
    const idadeMs = Date.now() - (state.ultimaAtualizacao ?? 0);
    if (idadeMs > STORAGE_TTL_DIAS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(chaveStorage(usuarioID));
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

function salvarNoStorage(usuarioID: number, state: EstadoPersistido) {
  try {
    localStorage.setItem(chaveStorage(usuarioID), JSON.stringify(state));
  } catch {
    // localStorage cheio/desabilitado — silencioso, persistencia e' "nice to have"
  }
}

function limparStorage(usuarioID: number) {
  try { localStorage.removeItem(chaveStorage(usuarioID)); } catch { /* ignore */ }
}

export interface UseChatOptions {
  apiBaseUrl: string;
  contexto: ContextoAssistente;
}

export interface UseChat {
  mensagens: Ref<MensagemChat[]>;
  carregando: Ref<boolean>;
  sessaoID: Ref<string | null>;
  iniciar(): Promise<void>;
  perguntar(texto: string): Promise<void>;
  darFeedback(mensagemId: string, feedback: 1 | 2, comentario?: string): Promise<void>;
  corrigir(mensagemId: string, correcao: string): Promise<{ ok: boolean; erro?: string }>;
  limpar(): void;
}

export function useChat(options: UseChatOptions): UseChat {
  const mensagens = ref<MensagemChat[]>([]);
  const carregando = ref(false);
  const sessaoID = ref<string | null>(null);

  // Restaura estado anterior (sobrevivente a navegacao entre modulos do MVC)
  const persistido = carregarDoStorage(options.contexto.usuarioID);
  if (persistido) {
    sessaoID.value = persistido.sessaoID;
    // Filtra eventuais mensagens em estado "carregando" — se a aba foi
    // fechada no meio de um stream, descarta a parcial.
    mensagens.value = persistido.mensagens.filter(m => !m.carregando);
  }

  // Auto-salva no localStorage a cada mudanca relevante (mensagens nova/editada,
  // sessaoID atualizado). Pula durante stream ativo pra evitar spam de write.
  watch([mensagens, sessaoID], () => {
    if (carregando.value) return;
    salvarNoStorage(options.contexto.usuarioID, {
      sessaoID: sessaoID.value,
      mensagens: mensagens.value.filter(m => !m.carregando && !m.erro),
      ultimaAtualizacao: Date.now()
    });
  }, { deep: true });

  const url = (path: string) => `${options.apiBaseUrl.replace(/\/$/, '')}${path}`;

  async function iniciar() {
    const resp = await fetch(url('/api/assistenteia/iniciar'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!resp.ok) throw new Error(`Falha ao iniciar sessao: ${resp.status}`);
    const data = (await resp.json()) as IniciarResponse;
    sessaoID.value = data.sessaoID;
  }

  async function perguntar(texto: string) {
    if (!sessaoID.value) await iniciar();

    const trimmed = texto.trim();
    if (trimmed.length === 0) return;

    const idUsuario = `u-${Date.now()}`;
    const idAssistente = `a-${Date.now() + 1}`;

    mensagens.value.push({
      id: idUsuario,
      papel: 'user',
      texto: trimmed,
      timestamp: Date.now()
    });

    mensagens.value.push({
      id: idAssistente,
      papel: 'assistant',
      texto: '',
      carregando: true,
      perguntaOriginal: trimmed, // necessario depois pra submeter correcao
      timestamp: Date.now()
    });

    carregando.value = true;
    try {
      const ordemNaSessao = mensagens.value.filter(m => m.papel === 'user').length;

      // Permissao e catalogo vem do +Vidas (cacheados por sessao de navegador).
      // Falha nisso nao interrompe a pergunta: sem catalogo, o chat responde
      // so' sobre o sistema, como antes.
      let controllersPermitidos: string[] = [];
      let consultasDisponiveis: ConsultaDisponivel[] = [];
      try {
        [controllersPermitidos, consultasDisponiveis] = await Promise.all([
          carregarControllersPermitidos(),
          carregarConsultasDisponiveis()
        ]);
      } catch {
        // degrada em silencio
      }

      const body: PerguntaApiRequest = {
        ...options.contexto,
        sessaoID: sessaoID.value!,
        pergunta: trimmed,
        ordemNaSessao,
        controllersPermitidos,
        consultasDisponiveis
      };

      await enviarTurno(body, idAssistente, 0);
    } catch (e) {
      atualizarMensagem(idAssistente, m => {
        m.carregando = false;
        m.erro = e instanceof Error ? e.message : 'Erro desconhecido';
      });
    } finally {
      carregando.value = false;
    }
  }

  // Maximo de idas ao modelo por pergunta. Cada ida pode pedir consultas; o
  // limite existe pra uma pergunta ambigua nao virar um laco de consultas.
  const MAX_VOLTAS_FERRAMENTA = 3;

  /**
   * Uma ida ao modelo. Quando ele pede consulta (evento "ferramenta"), o
   * navegador executa no +Vidas e reenvia a MESMA pergunta com os resultados,
   * ate o modelo responder em texto ou o limite de voltas ser atingido.
   */
  async function enviarTurno(body: PerguntaApiRequest, idAssistente: string, volta: number): Promise<void> {
    {
      const resp = await fetch(url('/api/assistenteia/perguntar/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include'
      });

      if (!resp.ok || !resp.body) {
        const erro = await resp.text().catch(() => '');
        atualizarMensagem(idAssistente, m => {
          m.carregando = false;
          m.erro = `Erro ${resp.status}: ${erro}`;
        });
        return;
      }

      // Marca o placeholder como nao-carregando assim que o primeiro delta
      // chegar, para o spinner sumir e o texto comecar a renderizar.
      let primeiraVez = true;
      let ferramentasPedidas: FerramentaSolicitada[] = [];
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Eventos SSE separados por \n\n. Ultimo pedaco pode estar incompleto;
        // guarda no buffer ate chegar o proximo chunk.
        const partes = buffer.split('\n\n');
        buffer = partes.pop() ?? '';

        for (const parte of partes) {
          if (!parte.startsWith('data: ')) continue;
          const json = parte.substring(6).trim();
          if (json.length === 0) continue;

          let evento: any;
          try { evento = JSON.parse(json); } catch { continue; }

          switch (evento.tipo) {
            case 'delta':
              if (primeiraVez) {
                atualizarMensagem(idAssistente, m => { m.carregando = false; });
                primeiraVez = false;
              }
              atualizarMensagem(idAssistente, m => { m.texto += evento.texto ?? ''; });
              break;

            case 'completo': {
              const data = evento.resposta as RespostaIA;
              atualizarMensagem(idAssistente, m => {
                m.texto = data.texto;
                m.modelo = data.modelo;
                m.origemResposta = data.origemResposta;
                m.carregando = false;
              });
              break;
            }

            case 'fim': {
              const data = evento.resposta as RespostaIA;
              atualizarMensagem(idAssistente, m => {
                m.modelo = data.modelo;
                m.origemResposta = data.origemResposta;
                m.carregando = false;
              });
              break;
            }

            case 'ferramenta':
              ferramentasPedidas = (evento.ferramentas ?? []) as FerramentaSolicitada[];
              break;

            case 'erro':
              atualizarMensagem(idAssistente, m => {
                m.carregando = false;
                m.erro = evento.mensagem ?? 'Erro desconhecido';
              });
              break;
          }
        }
      }

      // O modelo pediu consulta: executa no +Vidas e volta ao modelo com os
      // resultados, na mesma mensagem do assistente (o usuario ve uma resposta
      // unica, nao um vaivem).
      if (ferramentasPedidas.length > 0) {
        if (volta + 1 >= MAX_VOLTAS_FERRAMENTA) {
          atualizarMensagem(idAssistente, m => {
            m.carregando = false;
            m.erro = 'A consulta não pôde ser concluída. Tente detalhar melhor o pedido.';
          });
          return;
        }

        atualizarMensagem(idAssistente, m => {
          m.carregando = true;
          m.textoStatus = ferramentasPedidas.length === 1
            ? 'Consultando os dados...'
            : `Executando ${ferramentasPedidas.length} consultas...`;
        });

        const executadas: FerramentaExecutada[] = [];
        for (const f of ferramentasPedidas) {
          executadas.push(await executarFerramenta(f, sessaoID.value));
        }

        atualizarMensagem(idAssistente, m => { m.textoStatus = undefined; });

        // Acumula com o que ja foi executado nas voltas anteriores: o modelo
        // precisa do historico completo de tool_use/tool_result do turno.
        const anteriores = body.ferramentasExecutadas ?? [];
        await enviarTurno(
          { ...body, ferramentasExecutadas: [...anteriores, ...executadas] },
          idAssistente,
          volta + 1);
        return;
      }

      // Garantia: se nada chegou, marca como nao-carregando
      atualizarMensagem(idAssistente, m => { if (m.carregando) m.carregando = false; });
    }
  }

  async function darFeedback(mensagemId: string, feedback: 1 | 2, comentario?: string) {
    const msg = mensagens.value.find(m => m.id === mensagemId);
    if (!msg || !msg.interacaoID) {
      // Sem interacaoID — endpoint requer ID. Marcamos localmente apenas.
      atualizarMensagem(mensagemId, m => { m.feedback = feedback; });
      return;
    }

    await fetch(url('/api/assistenteia/feedback'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        interacaoID: msg.interacaoID,
        usuarioID: options.contexto.usuarioID,
        baseCliente: options.contexto.baseCliente,
        feedback,
        comentario: comentario ?? null
      })
    });

    atualizarMensagem(mensagemId, m => { m.feedback = feedback; });
  }

  async function corrigir(mensagemId: string, correcao: string) {
    const msg = mensagens.value.find(m => m.id === mensagemId);
    if (!msg || msg.papel !== 'assistant')
      return { ok: false, erro: 'Mensagem nao encontrada.' };

    if (!correcao.trim())
      return { ok: false, erro: 'Correcao vazia.' };

    try {
      const resp = await fetch(url('/api/assistenteia/corrigir'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interacaoID:       msg.interacaoID ?? null,
          sistema:           options.contexto.sistema,
          baseCliente:       options.contexto.baseCliente,
          plataforma:        options.contexto.plataforma,
          usuarioID:         options.contexto.usuarioID,
          usuarioNome:       options.contexto.usuarioNome,
          usuarioTipoID:     options.contexto.usuarioTipoID,
          controllerAtual:   options.contexto.controllerAtual,
          acaoAtual:         options.contexto.acaoAtual,
          perguntaOriginal:  msg.perguntaOriginal ?? '',
          respostaOriginal:  msg.texto,
          correcaoSugerida:  correcao.trim()
        })
      });

      if (!resp.ok) {
        const erro = await resp.text().catch(() => '');
        return { ok: false, erro: `Erro ${resp.status}: ${erro}` };
      }

      const data = await resp.json().catch(() => ({})) as { autoAprovada?: boolean };
      atualizarMensagem(mensagemId, m => {
        m.correcaoEnviada = true;
        m.correcaoAutoAprovada = data.autoAprovada === true;
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, erro: e instanceof Error ? e.message : 'Erro desconhecido' };
    }
  }

  function atualizarMensagem(id: string, mut: (m: MensagemChat) => void) {
    const idx = mensagens.value.findIndex(m => m.id === id);
    if (idx < 0) return;
    const copia = { ...mensagens.value[idx]! };
    mut(copia);
    mensagens.value.splice(idx, 1, copia);
  }

  function limpar() {
    mensagens.value = [];
    sessaoID.value = null;
    limparStorage(options.contexto.usuarioID);
  }

  return { mensagens, carregando, sessaoID, iniciar, perguntar, darFeedback, corrigir, limpar };
}
